import React from "react";
import type { Deal, Document } from "./types/redux";

/** Column definition for the inline deals table */
export interface ColumnDef {
  key: string;
  header: string;
  className?: string;
  cellClassName?: string;
  render: (deal: Deal) => React.ReactNode;
  exportValue: (deal: Deal) => string;
}

/** Get the best display name for a deal */
function getDealName(deal: Deal): string {
  return deal.dealName || deal.name || "--";
}

/** Get portfolio company name (or "Sensitive" mask) */
function getCompany(deal: Deal): string {
  if ((deal.sensitivity as string)?.toLowerCase() === "true") return "Sensitive";
  return deal.portfolioCompanyName || "--";
}

function getSector(deal: Deal): string {
  return deal.sector || "";
}

function getCountry(deal: Deal): string {
  return deal.country || "";
}

function getYear(deal: Deal): string {
  const y = deal.year as string | number | undefined;
  if (y == null || y === "") return "";
  return String(y);
}

function getStage(deal: Deal): string {
  return (deal.stage as string) || (deal.dealStage as string) || "";
}

/** Get document count from deal, cross-referencing payload-level documents */
function getDocCount(deal: Deal, documents?: Document[]): number {
  const count = deal.documentCount as number | undefined;
  if (typeof count === "number" && count > 0) return count;
  const embeddedDocs = deal.documents as unknown[] | undefined;
  if (Array.isArray(embeddedDocs) && embeddedDocs.length > 0) return embeddedDocs.length;
  if (documents && deal.dealId) {
    const matched = documents.filter((d) => d.dealId === deal.dealId);
    if (matched.length > 0) return matched.length;
  }
  return 0;
}

/** Get description text */
function getDescription(deal: Deal): string {
  return (deal.business as string) || (deal.description as string) || "";
}

function getDealType(deal: Deal): string {
  return (deal.dealType as string) || "PE";
}

function getDealTypeLabel(deal: Deal): string {
  return getDealType(deal) === "PC" ? "Credit" : "PE";
}

function dashIfEmpty(value: string): React.ReactNode {
  if (!value) return <span className="text-gray-400">--</span>;
  return value;
}

const DEAL_NAME_COLUMN: ColumnDef = {
  key: "dealName",
  header: "Deal Name",
  className: "min-w-[140px]",
  cellClassName: "truncate",
  render: (deal) => {
    const isPC = getDealType(deal) === "PC";
    return (
      <span
        className={`${isPC ? "text-cvc-pc bg-cvc-pcBg" : "text-cvc-blue bg-cvc-linkBg"} rounded-[3px] px-1 cursor-pointer hover:underline`}
      >
        {getDealName(deal)}
      </span>
    );
  },
  exportValue: getDealName,
};

const TYPE_COLUMN: ColumnDef = {
  key: "type",
  header: "Type",
  className: "w-[60px]",
  render: (deal) => {
    const isPC = getDealType(deal) === "PC";
    return (
      <span
        className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${isPC ? "bg-cvc-pcBg text-cvc-pc" : "bg-cvc-linkBg text-cvc-blue"}`}
      >
        {getDealTypeLabel(deal)}
      </span>
    );
  },
  exportValue: getDealTypeLabel,
};

const COMPANY_COLUMN: ColumnDef = {
  key: "company",
  header: "Company",
  className: "min-w-[120px]",
  cellClassName: "truncate",
  render: (deal) => getCompany(deal),
  exportValue: getCompany,
};

const SECTOR_COLUMN: ColumnDef = {
  key: "sector",
  header: "Sector",
  className: "min-w-[110px] hidden desktop:table-cell",
  cellClassName: "truncate hidden desktop:table-cell",
  render: (deal) => dashIfEmpty(getSector(deal)),
  exportValue: getSector,
};

const COUNTRY_COLUMN: ColumnDef = {
  key: "country",
  header: "Country",
  className: "min-w-[90px]",
  cellClassName: "truncate",
  render: (deal) => dashIfEmpty(getCountry(deal)),
  exportValue: getCountry,
};

const YEAR_COLUMN: ColumnDef = {
  key: "year",
  header: "Year",
  className: "w-[70px]",
  cellClassName: "truncate",
  render: (deal) => dashIfEmpty(getYear(deal)),
  exportValue: getYear,
};

const STAGE_COLUMN: ColumnDef = {
  key: "stage",
  header: "Stage",
  className: "min-w-[90px]",
  cellClassName: "truncate",
  render: (deal) => dashIfEmpty(getStage(deal)),
  exportValue: getStage,
};

const DESCRIPTION_COLUMN: ColumnDef = {
  key: "description",
  header: "Description",
  className: "min-w-[200px] hidden desktop:table-cell",
  cellClassName: "truncate hidden desktop:table-cell",
  render: (deal) => {
    const desc = getDescription(deal);
    if (!desc) return <span className="text-gray-400">No description</span>;
    return <span title={desc}>{desc}</span>;
  },
  exportValue: getDescription,
};

/** The docs column is payload-dependent, so it is built per render rather than shared. */
function docsColumn(documents?: Document[]): ColumnDef {
  return {
    key: "docs",
    header: "Docs",
    className: "w-[60px] text-center",
    render: (deal) => {
      const count = getDocCount(deal, documents);
      if (count === 0) return <span className="text-gray-400">--</span>;
      return (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-cvc-lb2 text-cvc-blue rounded-full">
          {count}
        </span>
      );
    },
    exportValue: (deal) => String(getDocCount(deal, documents)),
  };
}

export function buildDealColumns(documents?: Document[]): ColumnDef[] {
  return [
    DEAL_NAME_COLUMN,
    TYPE_COLUMN,
    COMPANY_COLUMN,
    SECTOR_COLUMN,
    COUNTRY_COLUMN,
    YEAR_COLUMN,
    STAGE_COLUMN,
    docsColumn(documents),
    DESCRIPTION_COLUMN,
  ];
}
